import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {leastCommonMultiple} from '@application/helper/textile-data/colored-yarn-set/least-common-multiple';
import {UnsavedChangesNotifierService} from '@application/helper/unsaved-changes-notifier/unsaved-changes-notifier.service';
import {HorizontalPositionOfCreelPosition} from '@domain/textile-data/creel/horizontal-position-of-creel-position';
import {CreelPositionWithColorsAndYarnTypes} from '@presentation/pages/textile-data/colored-yarn-set/add/creel-position-with-colors-and-yarn-types';
import {OverviewListYarnType} from '@presentation/pages/textile-data/yarn-type/overview/overview-list-yarn-type';
import {AssertionUtils, BaseComponent, Color} from '@vdw/angular-component-library';
import {each, findIndex, isNil, map, range, sumBy} from 'lodash-es';
import {debounceTime, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-creel-preview',
  templateUrl: './creel-preview.component.html',
  styleUrls: ['./creel-preview.component.scss']
})
export class CreelPreviewComponent extends BaseComponent implements OnChanges {
  @ViewChild('scrollCreelPreview') public scrollCreelPreview;
  @Input() public creelPositionWithColorsAndYarnTypes: CreelPositionWithColorsAndYarnTypes;
  @Input() public nameOfColorSet: string;
  @Input() public nameOfYarnSet: string;
  public readonly minimumAmountOfDents = 1;
  public readonly maximumAmountOfDents = 99999;
  public showCreelPreviewForm: FormGroup<{numberOfDentsToShow: FormControl<number>}>;

  public patternsForCreelPreview: {
    color?: Color;
    yarnType?: OverviewListYarnType;
    dent: number;
  }[];

  private uniqueColors: Color[] = [];
  private uniqueYarnTypes: OverviewListYarnType[] = [];

  public constructor(
    private readonly formBuilder: FormBuilder,
    private unsavedChangesNotifierService: UnsavedChangesNotifierService
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.creelPositionWithColorsAndYarnTypes) {
      if (isNil(this.showCreelPreviewForm)) {
        this.initializeShowCreelPreviewForm();
      }

      if (this.isCreelPreview()) {
        const maximumDentOfCreelPositionForColorPatternOfCreelPosition = this.canShowPreviewForColorSet() ? this.getMaximumDentOfCreelPosition(this.getColorPatternOfCreelPosition()) : 1;
        const maximumDentOfCreelPositionForYarnTypePatternOfCreelPosition = this.canShowPreviewForYarnSet() ? this.getMaximumDentOfCreelPosition(this.getYarnTypePatternOfCreelPosition()) : 1;

        this.showCreelPreviewForm.controls.numberOfDentsToShow.setValue(
          leastCommonMultiple(maximumDentOfCreelPositionForColorPatternOfCreelPosition, maximumDentOfCreelPositionForYarnTypePatternOfCreelPosition)
        );
      }

      this.generatePatternsForNumberOfDents();
    }
  }

  public setScrollPosition(): void {
    this.scrollCreelPreview.scrollToIndex(this.showCreelPreviewForm.value.numberOfDentsToShow - 1);
    this.unsavedChangesNotifierService.notifyChangesToOthers();
  }

  public getUniqueColors(): Color[] {
    return this.uniqueColors;
  }

  public getUniqueYarnTypes(): OverviewListYarnType[] {
    return this.uniqueYarnTypes;
  }

  public isCreelPreview(): boolean {
    return this.canShowPreviewForYarnSet() || this.canShowPreviewForColorSet();
  }

  public canShowPreviewForYarnSet(): boolean {
    return !isNil(this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes);
  }

  public canShowPreviewForColorSet(): boolean {
    return !isNil(this.creelPositionWithColorsAndYarnTypes.creelPositionForColors);
  }

  public canShowLabelForDent(dent: number): boolean {
    return (dent + 1) % 5 === 0 || dent === this.showCreelPreviewForm.value.numberOfDentsToShow - 1 || dent === 0;
  }

  public getItemViewIndexForColor(color: Color): number {
    return findIndex(this.uniqueColors, color) + 1;
  }

  public getItemViewIndexForYarnType(yarnType: OverviewListYarnType): number {
    return findIndex(this.uniqueYarnTypes, yarnType) + 1;
  }

  private initializeShowCreelPreviewForm(): void {
    this.showCreelPreviewForm = this.formBuilder.group({
      numberOfDentsToShow: this.formBuilder.control(this.minimumAmountOfDents, [Validators.required, Validators.min(this.minimumAmountOfDents), Validators.max(this.maximumAmountOfDents)])
    });

    this.showCreelPreviewForm.controls.numberOfDentsToShow.valueChanges.pipe(debounceTime(500), takeUntil(this.unSubscribeOnViewDestroy)).subscribe((numberOfDentsToShow: number) => {
      this.numberOfDentsToShowAdjusted(numberOfDentsToShow);
    });
  }

  private numberOfDentsToShowAdjusted(newValue: number): void {
    if (newValue >= this.minimumAmountOfDents && newValue <= this.maximumAmountOfDents) {
      this.generatePatternsForNumberOfDents(newValue);
    }
  }

  private getColorPatternOfCreelPosition(): HorizontalPositionOfCreelPosition[] {
    return this.creelPositionWithColorsAndYarnTypes.creelPositionForColors.horizontalPositionsOfCreelPosition;
  }

  private getYarnTypePatternOfCreelPosition(): HorizontalPositionOfCreelPosition[] {
    return this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes.horizontalPositionsOfCreelPosition;
  }

  private generatePatternsForNumberOfDents(userInputValue?: number): void {
    let numberOfDentsToShow = 0;

    if (AssertionUtils.isNullOrUndefined(userInputValue)) {
      if (!AssertionUtils.isNullOrUndefined(this.creelPositionWithColorsAndYarnTypes.creelPositionForColors)) {
        this.creelPositionWithColorsAndYarnTypes.creelPositionForColors.horizontalPositionsOfCreelPosition.forEach((creelPosition: HorizontalPositionOfCreelPosition) => {
          numberOfDentsToShow += creelPosition.amount;
        });
      }

      if (!AssertionUtils.isNullOrUndefined(this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes)) {
        this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes.horizontalPositionsOfCreelPosition.forEach((creelPosition: HorizontalPositionOfCreelPosition) => {
          numberOfDentsToShow += creelPosition.amount;
        });
      }
    } else {
      numberOfDentsToShow = userInputValue;
    }

    this.patternsForCreelPreview = map(range(numberOfDentsToShow), (dent: number) => {
      return {
        dent
      };
    });

    if (this.canShowPreviewForColorSet()) {
      this.uniqueColors = this.creelPositionWithColorsAndYarnTypes.creelPositionForColors.getUniqueItems() as Color[];
      const patternForColorSet = this.creelPositionWithColorsAndYarnTypes.creelPositionForColors.getPatternForGivenNumberOfDents(numberOfDentsToShow) as Color[];
      each(patternForColorSet, (color: Color, index: number) => {
        this.patternsForCreelPreview[index].color = color;
      });
    }

    if (this.canShowPreviewForYarnSet()) {
      this.uniqueYarnTypes = this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes.getUniqueItems() as OverviewListYarnType[];
      const patternForYarnSet = this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes.getPatternForGivenNumberOfDents(numberOfDentsToShow) as OverviewListYarnType[];
      each(patternForYarnSet, (yarnType: OverviewListYarnType, index: number) => {
        this.patternsForCreelPreview[index].yarnType = yarnType;
      });
    }
  }

  private getMaximumDentOfCreelPosition(creelPosition: HorizontalPositionOfCreelPosition[]): number {
    return sumBy(creelPosition, (horizontalPositionOfCreelPosition: HorizontalPositionOfCreelPosition) => {
      return horizontalPositionOfCreelPosition.getAmountOfDentsInHorizontalPositionOfCreelPosition();
    });
  }
}
